import { useState, useCallback, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  className?: string;
  fallbackSrc?: string;
  aspectRatio?: string;
  /** Called when image is detected as a tiny icon/placeholder (naturalWidth < threshold) */
  onTinyImage?: () => void;
}

/**
 * Transforms image URLs to use optimized versions where available.
 */
function getOptimizedSrcSet(src: string, widths: number[]): string {
  if (src.includes('ddfcdn.realtor.ca')) {
    // ddfcdn supports highres / medres / lowres path segments
    const medSrc = src.replace('/highres/', '/medres/');
    const lowSrc = src.replace('/highres/', '/lowres/');
    return `${lowSrc} 320w, ${medSrc} 640w, ${src} 1024w`;
  }
  
  if (src.includes('realtor.ca') && !src.includes('ddfcdn')) {
    return widths
      .map(w => {
        const url = src.includes('?') 
          ? `${src}&wid=${w}` 
          : `${src}?wid=${w}`;
        return `${url} ${w}w`;
      })
      .join(', ');
  }
  
  if (src.includes('unsplash.com')) {
    return widths
      .map(w => {
        const url = new URL(src);
        url.searchParams.set('w', w.toString());
        url.searchParams.set('q', '80');
        url.searchParams.set('fm', 'webp');
        url.searchParams.set('fit', 'crop');
        return `${url.toString()} ${w}w`;
      })
      .join(', ');
  }
  
  if (src.includes('supabase')) {
    return widths
      .map(w => {
        const url = src.includes('?')
          ? `${src}&width=${w}`
          : `${src}?width=${w}`;
        return `${url} ${w}w`;
      })
      .join(', ');
  }
  
  return '';
}

function getOptimizedDefaultSrc(src: string): string {
  // For ddfcdn, default to medres so the browser doesn't start with the huge highres file
  if (src.includes('ddfcdn.realtor.ca') && src.includes('/highres/')) {
    return src.replace('/highres/', '/medres/');
  }
  if (src.includes('unsplash.com')) {
    const url = new URL(src);
    url.searchParams.set('fm', 'webp');
    url.searchParams.set('q', '80');
    return url.toString();
  }
  return src;
}

// Minimum dimensions to consider an image as a "real" property photo
// Icons/placeholders uploaded by agents are typically < 200px
const MIN_REAL_IMAGE_SIZE = 200;

const DEFAULT_FALLBACK = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop&fm=webp';
const DEFAULT_WIDTHS = [320, 480, 640, 800, 1024, 1280];

export function OptimizedImage({
  src,
  alt,
  width = 400,
  height = 300,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px',
  loading = 'lazy',
  fetchPriority = 'auto',
  className,
  fallbackSrc = DEFAULT_FALLBACK,
  aspectRatio,
  onTinyImage,
}: OptimizedImageProps) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const prevSrcRef = useRef(src);
  
  useEffect(() => {
    // Only reset loading state when src actually changes to a different URL
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setImgSrc(src);
      setHasError(false);
      setIsLoaded(false);
    }
  }, [src]);

  // Check if image was already cached (complete before onLoad fires)
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [imgSrc]);
  
  const handleError = useCallback(() => {
    if (!hasError && fallbackSrc) {
      setHasError(true);
      setImgSrc(fallbackSrc);
    }
  }, [hasError, fallbackSrc]);

  const handleLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setIsLoaded(true);
    // Detect tiny/icon images that aren't real property photos
    if (
      img.naturalWidth > 0 &&
      img.naturalHeight > 0 &&
      img.naturalWidth < MIN_REAL_IMAGE_SIZE &&
      img.naturalHeight < MIN_REAL_IMAGE_SIZE &&
      !hasError &&
      onTinyImage
    ) {
      onTinyImage();
    }
  }, [hasError, onTinyImage]);

  const srcSet = getOptimizedSrcSet(imgSrc, DEFAULT_WIDTHS);
  const optimizedSrc = getOptimizedDefaultSrc(imgSrc);

  return (
    <img
      ref={imgRef}
      src={optimizedSrc}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      {...({ fetchpriority: fetchPriority } as Record<string, string>)}
      decoding="async"
      // Note: do NOT set crossOrigin="anonymous" — ddfcdn.realtor.ca doesn't
      // support CORS headers, so setting it breaks image loading entirely.
      onError={handleError}
      onLoad={handleLoad}
      className={cn(
        'object-cover transition-opacity duration-300',
        isLoaded ? 'opacity-100' : 'opacity-0',
        aspectRatio && `aspect-[${aspectRatio}]`,
        className
      )}
      style={{
        aspectRatio: aspectRatio || `${width}/${height}`,
      }}
    />
  );
}

export function useOptimizedImage(src: string, widths = DEFAULT_WIDTHS) {
  return {
    src: getOptimizedDefaultSrc(src),
    srcSet: getOptimizedSrcSet(src, widths),
    sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px',
  };
}
